import React, { useState, useRef, useEffect, useCallback } from "react";
import { showLovableToast } from "../LovableToast/showLovableToast";
import "./SharePopover.scss";

const buildTwitterUrl = (url) =>
  url
    ? `http://twitter.com/share?url=${encodeURIComponent(url)}`
    : null;

const buildFacebookUrl = (url) =>
  url
    ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    : null;

/**
 * Lovable share menu — Copy link / X / Facebook (same pattern as post detail).
 * Trigger keeps icon + “Share” label for feed action rows (Alerts, etc.).
 */
const SharePopover = ({
  url,
  show = false,
  onToggle,
  popoverRef,
  className = "",
  style,
}) => {
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const internalPopoverRef = useRef(null);
  const resolvedPopoverRef = popoverRef || internalPopoverRef;
  const twitterUrl = buildTwitterUrl(url);
  const facebookUrl = buildFacebookUrl(url);

  const close = useCallback(() => {
    if (show && onToggle) {
      onToggle();
    }
  }, [show, onToggle]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, close]);

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      showLovableToast("Link copied");
      window.setTimeout(() => setCopied(false), 2000);
      close();
    } catch {
      setCopied(false);
    }
  };

  const handleTriggerClick = (event) => {
    event.stopPropagation();
    onToggle?.();
  };

  return (
    <div
      ref={rootRef}
      className={["link-social dropdown-share", className]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      <button
        type="button"
        className="dropdown-share__trigger"
        onClick={handleTriggerClick}
        aria-expanded={show}
        aria-haspopup="menu"
      >
        <span className="post-social-action__icon" aria-hidden="true">
          <i className="material-icons share">share</i>
        </span>
        <span className="dropdown-share__label post-social-action__label">
          Share
        </span>
      </button>

      <div
        ref={resolvedPopoverRef}
        className={[
          "share-popover",
          show && "share-popover--open",
        ]
          .filter(Boolean)
          .join(" ")}
        role="menu"
        aria-label="Share options"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={[
            "share-popover__item",
            copied && "share-popover__item--copied",
          ]
            .filter(Boolean)
            .join(" ")}
          role="menuitem"
          onClick={handleCopy}
        >
          <span className="material-icons" aria-hidden="true">
            {copied ? "check_circle" : "link"}
          </span>
          {copied ? "Copied" : "Copy link"}
        </button>

        {twitterUrl && (
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="share-popover__item"
            role="menuitem"
            onClick={close}
          >
            <i className="fab fa-twitter" aria-hidden="true" />
            Share on X
          </a>
        )}

        {facebookUrl && (
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="share-popover__item"
            role="menuitem"
            onClick={close}
          >
            <span className="material-icons" aria-hidden="true">
              facebook
            </span>
            Facebook
          </a>
        )}
      </div>
    </div>
  );
};

export default SharePopover;
